(() => {
  const timers = new WeakMap();
  const REWARD_ACCESS_KEY = 'eg-luce-reward-access-v1';
  const REWARD_AUTOPLAY_KEY = 'eg-luce-reward-autoplay-v1';

  const pad = (value) => String(Math.max(0, value)).padStart(2, '0');

  const storage = {
    get(type, key) {
      try {
        return window[type].getItem(key);
      } catch (_error) {
        return null;
      }
    },
    set(type, key, value) {
      try {
        window[type].setItem(key, value);
      } catch (_error) {
        // Storage can be unavailable in private browsing or strict privacy modes.
      }
    }
  };

  function initCountdown(root) {
    const countdown = root.querySelector('[data-eg-countdown]');
    if (!countdown) return;

    const rawDate = countdown.dataset.launchDate;
    const target = rawDate ? new Date(rawDate).getTime() : Number.NaN;

    if (!Number.isFinite(target)) {
      countdown.classList.add('is-hidden');
      return;
    }

    const fields = {
      days: countdown.querySelector('[data-eg-days]'),
      hours: countdown.querySelector('[data-eg-hours]'),
      minutes: countdown.querySelector('[data-eg-minutes]'),
      seconds: countdown.querySelector('[data-eg-seconds]')
    };

    const update = () => {
      const distance = target - Date.now();

      if (distance <= 0) {
        Object.values(fields).forEach((field) => {
          if (field) field.textContent = '00';
        });
        countdown.dataset.complete = 'true';
        const label = countdown.querySelector('.eg-countdown__label');
        if (label) label.textContent = 'La luce è arrivata';
        const timer = timers.get(root);
        if (timer) window.clearInterval(timer);
        return;
      }

      const days = Math.floor(distance / 86400000);
      const hours = Math.floor((distance % 86400000) / 3600000);
      const minutes = Math.floor((distance % 3600000) / 60000);
      const seconds = Math.floor((distance % 60000) / 1000);

      if (fields.days) fields.days.textContent = pad(days);
      if (fields.hours) fields.hours.textContent = pad(hours);
      if (fields.minutes) fields.minutes.textContent = pad(minutes);
      if (fields.seconds) fields.seconds.textContent = pad(seconds);
    };

    countdown.classList.remove('is-hidden');
    update();
    const timer = window.setInterval(update, 1000);
    timers.set(root, timer);
  }

  function initPointerExperience(root) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const finePointer = window.matchMedia('(pointer: fine)').matches;
    if (reducedMotion || !finePointer) return;

    const depthElements = [...root.querySelectorAll('[data-eg-depth]')];
    const tilt = root.querySelector('[data-eg-tilt]');
    let frameId = 0;
    let point = { x: 0, y: 0 };

    const render = () => {
      frameId = 0;

      depthElements.forEach((element) => {
        const depth = Number.parseFloat(element.dataset.egDepth || '0');
        const x = point.x * depth;
        const y = point.y * depth * 0.72;
        element.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      });

      if (tilt) {
        const rotateY = point.x * 1.35;
        const rotateX = point.y * -1.05;
        tilt.style.transform = `rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(0)`;
      }
    };

    const onMove = (event) => {
      const rect = root.getBoundingClientRect();
      const xRatio = (event.clientX - rect.left) / rect.width;
      const yRatio = (event.clientY - rect.top) / rect.height;

      point = {
        x: (xRatio - 0.5) * 2,
        y: (yRatio - 0.5) * 2
      };

      root.style.setProperty('--eg-mx', `${xRatio * 100}%`);
      root.style.setProperty('--eg-my', `${yRatio * 100}%`);

      if (!frameId) frameId = window.requestAnimationFrame(render);
    };

    const onLeave = () => {
      point = { x: 0, y: 0 };
      root.style.setProperty('--eg-mx', '50%');
      root.style.setProperty('--eg-my', '50%');
      if (!frameId) frameId = window.requestAnimationFrame(render);
    };

    root.addEventListener('pointermove', onMove, { passive: true });
    root.addEventListener('pointerleave', onLeave, { passive: true });

    root._egCleanupPointer = () => {
      root.removeEventListener('pointermove', onMove);
      root.removeEventListener('pointerleave', onLeave);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }

  function initReward(root) {
    const dialog = root.querySelector('[data-eg-reward]');
    if (!dialog) return;

    const panel = dialog.querySelector('[data-eg-reward-panel]');
    const form = root.querySelector('.eg-signup__form');
    const feedback = root.querySelector('[data-eg-form-feedback]');
    const submitButton = root.querySelector('[data-eg-signup-submit]');
    const replayButtons = [...root.querySelectorAll('[data-eg-reward-replay]')];
    const closeButtons = [...dialog.querySelectorAll('[data-eg-reward-close]')];
    const initialSuccess = root.querySelector('[data-eg-signup-success]');
    const autoplayKey = `${REWARD_AUTOPLAY_KEY}:${root.dataset.sectionId || 'main'}`;
    let nativeFallback = false;
    let closingTimer = 0;
    let openingTimer = 0;

    const setUnlocked = () => {
      root.classList.add('has-reward-access');
      storage.set('localStorage', REWARD_ACCESS_KEY, 'true');
    };

    const openReward = () => {
      if (closingTimer) {
        window.clearTimeout(closingTimer);
        closingTimer = 0;
      }
      dialog.classList.remove('is-closing');

      if (!dialog.open) {
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }
      }

      root.classList.add('is-reward-open');
      document.documentElement.classList.add('eg-reward-lock');

      window.setTimeout(() => {
        const closeButton = dialog.querySelector('[data-eg-reward-close]');
        if (closeButton) closeButton.focus({ preventScroll: true });
      }, 560);
    };

    const closeReward = () => {
      if (!dialog.open || dialog.classList.contains('is-closing')) return;
      dialog.classList.add('is-closing');

      closingTimer = window.setTimeout(() => {
        if (dialog.open && typeof dialog.close === 'function') {
          dialog.close();
        } else {
          dialog.removeAttribute('open');
        }
        dialog.classList.remove('is-closing');
        root.classList.remove('is-reward-open');
        document.documentElement.classList.remove('eg-reward-lock');
        const replayButton = replayButtons[0];
        if (replayButton) replayButton.focus({ preventScroll: true });
      }, 340);
    };

    const unlockAndOpen = () => {
      setUnlocked();
      storage.set('sessionStorage', autoplayKey, 'true');
      window.clearTimeout(openingTimer);
      openingTimer = window.setTimeout(openReward, 420);
    };

    const showFeedback = (element) => {
      if (!feedback || !element) return;
      feedback.replaceChildren(element.cloneNode(true));
    };

    const onSubmit = async (event) => {
      if (nativeFallback || !form || typeof window.fetch !== 'function') return;
      if (!form.checkValidity()) return;

      event.preventDefault();
      submitButton?.classList.add('is-loading');
      submitButton?.setAttribute('aria-busy', 'true');
      if (submitButton) submitButton.disabled = true;

      try {
        const response = await window.fetch(form.action, {
          method: (form.method || 'post').toUpperCase(),
          body: new FormData(form),
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) throw new Error(`Signup request failed: ${response.status}`);

        const html = await response.text();
        const returnedDocument = new DOMParser().parseFromString(html, 'text/html');
        const returnedSuccess = returnedDocument.querySelector('[data-eg-signup-success]');
        const returnedError = returnedDocument.querySelector('.eg-form-message--error');

        if (returnedSuccess) {
          showFeedback(returnedSuccess);
          form.reset();
          unlockAndOpen();
          return;
        }

        if (returnedError) {
          showFeedback(returnedError);
          return;
        }

        throw new Error('Signup result could not be confirmed.');
      } catch (_error) {
        nativeFallback = true;
        form.submit();
      } finally {
        submitButton?.classList.remove('is-loading');
        submitButton?.removeAttribute('aria-busy');
        if (submitButton) submitButton.disabled = false;
      }
    };

    const onBackdropClick = (event) => {
      if (event.target === dialog || (panel && !panel.contains(event.target))) {
        closeReward();
      }
    };

    const onCancel = (event) => {
      event.preventDefault();
      closeReward();
    };

    const onNativeClose = () => {
      root.classList.remove('is-reward-open');
      document.documentElement.classList.remove('eg-reward-lock');
    };

    form?.addEventListener('submit', onSubmit);
    replayButtons.forEach((button) => button.addEventListener('click', openReward));
    closeButtons.forEach((button) => button.addEventListener('click', closeReward));
    dialog.addEventListener('click', onBackdropClick);
    dialog.addEventListener('cancel', onCancel);
    dialog.addEventListener('close', onNativeClose);

    if (initialSuccess) {
      setUnlocked();
      if (!storage.get('sessionStorage', autoplayKey)) {
        storage.set('sessionStorage', autoplayKey, 'true');
        openingTimer = window.setTimeout(openReward, 620);
      }
    } else if (storage.get('localStorage', REWARD_ACCESS_KEY) === 'true') {
      setUnlocked();
    }

    root._egCleanupReward = () => {
      window.clearTimeout(closingTimer);
      window.clearTimeout(openingTimer);
      form?.removeEventListener('submit', onSubmit);
      replayButtons.forEach((button) => button.removeEventListener('click', openReward));
      closeButtons.forEach((button) => button.removeEventListener('click', closeReward));
      dialog.removeEventListener('click', onBackdropClick);
      dialog.removeEventListener('cancel', onCancel);
      dialog.removeEventListener('close', onNativeClose);
      document.documentElement.classList.remove('eg-reward-lock');
    };
  }

  function initRoot(root) {
    if (!root || root.dataset.egInitialized === 'true') return;
    root.dataset.egInitialized = 'true';

    initCountdown(root);
    initPointerExperience(root);
    initReward(root);

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => root.classList.add('is-ready'));
    });
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-eg-root]').forEach(initRoot);
  }

  function destroyRoot(root) {
    const timer = timers.get(root);
    if (timer) window.clearInterval(timer);
    timers.delete(root);

    if (typeof root._egCleanupPointer === 'function') {
      root._egCleanupPointer();
    }

    if (typeof root._egCleanupReward === 'function') {
      root._egCleanupReward();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-eg-root]');
    if (root) destroyRoot(root);
  });
})();
